/**
 * Soundeffekte + Hintergrundmusik, vollständig lokal per Web Audio erzeugt
 * (keine Dateien, kein CDN, offline). Sound-Set steuert die Effekte; die
 * Musik-Bibliothek („Musik-Bed") ist davon unabhängig wählbar. Alles wird über
 * eine User-Geste (Umschalten) aktiviert. Eigene hochgeladene Dateien haben
 * Vorrang vor den Synth-Sounds/-Beds.
 */
export type SoundPack = 'off' | 'arcade' | 'sanft';

/** Wählbare, generativ erzeugte Musik-Bibliotheken (modern, ansprechend). */
export type MusicBed = 'off' | 'lounge' | 'synthwave' | 'energetic' | 'deephouse' | 'ambient';

export const MUSIC_BEDS: { id: MusicBed; label: string; description: string }[] = [
  { id: 'off', label: 'Musik aus', description: 'Keine Hintergrundmusik' },
  { id: 'lounge', label: 'Lo-Fi Lounge', description: 'Warm, entspannt, jazzige Akkorde' },
  { id: 'synthwave', label: 'Synthwave', description: 'Retro-80er, treibender Bass' },
  { id: 'energetic', label: 'Energetic', description: 'EDM-Puls, Four-on-the-Floor' },
  { id: 'deephouse', label: 'Deep House', description: 'Tiefer Groove, Chord-Stabs' },
  { id: 'ambient', label: 'Ambient', description: 'Ruhig, schwebende Flächen' },
];

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let musicGain: GainNode | null = null;
let pack: SoundPack = 'off';
let bed: MusicBed = 'off';
let musicWanted = false;
let noiseBuf: AudioBuffer | null = null;

// Optionale, hochgeladene Sounddateien (überschreiben die Synth-Sounds/-Beds).
let custom: { music?: string; countdown?: string; reveal?: string } = {};
let musicAudio: HTMLAudioElement | null = null;

export function setCustomSounds(s: { music?: string; countdown?: string; reveal?: string } | null): void {
  custom = s ?? {};
}

function playFile(url: string, volume = 0.9): void {
  try {
    const a = new Audio(url);
    a.volume = volume;
    void a.play().catch(() => {});
  } catch {
    /* ignore */
  }
}

/** Erzeugt (einmalig) den AudioContext + Master/Music-Busse. Nicht an `pack` gebunden. */
function ensureCtx(): boolean {
  if (!ctx) {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return false;
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.5;
    master.connect(ctx.destination);
    musicGain = ctx.createGain();
    musicGain.gain.value = 0.0; // wird beim Start sanft eingeblendet
    musicGain.connect(ctx.destination);
  }
  void ctx.resume();
  return true;
}

function whiteNoise(): AudioBuffer {
  if (noiseBuf) return noiseBuf;
  const len = ctx!.sampleRate * 2;
  const buf = ctx!.createBuffer(1, len, ctx!.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  noiseBuf = buf;
  return buf;
}

export function getPack(): SoundPack {
  return pack;
}

export function setPack(p: SoundPack): void {
  pack = p;
  if (p === 'off') return;
  ensureCtx();
  // Sofortiges akustisches Feedback beim Aktivieren.
  chord([523.25, 659.25, 783.99], 0.25, 0.12);
}

export function getMusicBed(): MusicBed {
  return bed;
}

export function setMusicBed(b: MusicBed): void {
  bed = b;
  if (b === 'off') {
    stopMusic();
    return;
  }
  ensureCtx();
  if (musicWanted) startMusic();
}

// ─── Effekt-Synthese ─────────────────────────────────────────────────────────

function tone(freq: number, dur: number, opts: { type?: OscillatorType; gain?: number; at?: number; glideTo?: number } = {}): void {
  if (!ctx || !master) return;
  const t0 = opts.at ?? ctx.currentTime;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = opts.type ?? 'sine';
  o.frequency.setValueAtTime(freq, t0);
  if (opts.glideTo) o.frequency.exponentialRampToValueAtTime(opts.glideTo, t0 + dur);
  const peak = opts.gain ?? 0.2;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g);
  g.connect(master);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}

function chord(freqs: number[], dur: number, gain = 0.15): void {
  if (pack === 'off' || !ensureCtx()) return;
  for (const f of freqs) tone(f, dur, { type: pack === 'arcade' ? 'square' : 'sine', gain });
}

/** Countdown-Ton je Schritt (3 → 2 → 1): steigende Tonhöhe. */
export function playCountTick(secondsLeft: number): void {
  if (pack === 'off') return;
  if (custom.countdown) return playFile(custom.countdown);
  if (!ensureCtx()) return;
  const map: Record<number, number> = { 3: 440, 2: 554.37, 1: 659.25 };
  tone(map[secondsLeft] ?? 523.25, 0.18, { type: pack === 'arcade' ? 'square' : 'triangle', gain: 0.25 });
}

/** „Los!" beim Start der Antwortphase. */
export function playGo(): void {
  if (pack === 'off' || !ensureCtx()) return;
  tone(523.25, 0.35, { type: pack === 'arcade' ? 'square' : 'sine', gain: 0.28, glideTo: 1046.5 });
}

/** Gong/Chime bei der Auflösung. */
export function playReveal(): void {
  if (pack === 'off') return;
  if (custom.reveal) return playFile(custom.reveal);
  if (!ensureCtx()) return;
  const t = ctx!.currentTime;
  tone(196, 1.6, { type: 'sine', gain: 0.35, at: t });
  tone(392, 1.4, { type: 'sine', gain: 0.16, at: t });
  tone(587.33, 1.2, { type: 'sine', gain: 0.09, at: t + 0.02 });
}

/** Fanfare am Ende (Podium). */
export function playFanfare(): void {
  if (pack === 'off' || !ensureCtx()) return;
  const seq = [523.25, 659.25, 783.99, 1046.5];
  const t = ctx!.currentTime;
  seq.forEach((f, i) => tone(f, 0.5, { type: pack === 'arcade' ? 'square' : 'triangle', gain: 0.28, at: t + i * 0.16 }));
  chord([523.25, 659.25, 783.99, 1046.5], 0.9, 0.12);
}

// ─── Musik-Beds (generativer Step-Sequencer, Web-Audio-Clock) ────────────────

const midi = (m: number): number => 440 * Math.pow(2, (m - 69) / 12);

interface BedDef {
  bpm: number;
  /** Akkordprogression (MIDI-Noten je Takt). */
  chords: number[][];
  /** Bass-MIDI-Note relativ pro Takt (meist Grundton −12/−24). */
  bassOctave: number;
  /** Drum-Pattern (16tel-Raster, Schrittindizes 0..15). */
  kick: number[];
  hat: number[];
  snare: number[];
  /** Arp-Typ + Dichte (jede n-te 16tel spielt eine Akkordnote). */
  arpEvery: number;
  arpType: OscillatorType;
  padType: OscillatorType;
  pad: boolean;
  swing: boolean;
}

const BEDS: Record<Exclude<MusicBed, 'off'>, BedDef> = {
  lounge: {
    bpm: 80,
    // Dm9 – G13 – Cmaj7 – Am7 (jazzig, warm)
    chords: [[62, 65, 69, 72], [55, 59, 65, 69], [60, 64, 67, 71], [57, 60, 64, 67]],
    bassOctave: -24,
    kick: [0, 8],
    hat: [2, 6, 10, 14],
    snare: [8],
    arpEvery: 4,
    arpType: 'triangle',
    padType: 'sawtooth',
    pad: true,
    swing: true,
  },
  synthwave: {
    bpm: 102,
    // Am – F – C – G (klassisch 80er)
    chords: [[57, 60, 64], [53, 57, 60], [48, 52, 55, 60], [55, 59, 62]],
    bassOctave: -12,
    kick: [0, 4, 8, 12],
    hat: [2, 6, 10, 14],
    snare: [4, 12],
    arpEvery: 2,
    arpType: 'sawtooth',
    padType: 'sawtooth',
    pad: true,
    swing: false,
  },
  energetic: {
    bpm: 126,
    // f-moll-ish, hymnische EDM-Progression
    chords: [[53, 57, 60], [60, 64, 67], [55, 58, 62], [57, 60, 64]],
    bassOctave: -24,
    kick: [0, 4, 8, 12],
    hat: [2, 6, 10, 14],
    snare: [4, 12],
    arpEvery: 2,
    arpType: 'square',
    padType: 'sawtooth',
    pad: true,
    swing: false,
  },
  deephouse: {
    bpm: 122,
    // Cm7 – Fm7 – Abmaj7 – Bb (deep)
    chords: [[60, 63, 67, 70], [53, 56, 60, 63], [56, 60, 63, 67], [58, 62, 65]],
    bassOctave: -24,
    kick: [0, 4, 8, 12],
    hat: [2, 6, 10, 14],
    snare: [],
    arpEvery: 8, // Chord-Stabs auf Offbeats statt Arp
    arpType: 'sawtooth',
    padType: 'triangle',
    pad: true,
    swing: false,
  },
  ambient: {
    bpm: 60,
    // Weite, schwebende Dur-Flächen
    chords: [[60, 64, 67, 71], [57, 62, 65, 69], [55, 59, 62, 67], [53, 57, 60, 64]],
    bassOctave: -24,
    kick: [],
    hat: [],
    snare: [],
    arpEvery: 8,
    arpType: 'sine',
    padType: 'sine',
    pad: true,
    swing: false,
  },
};

let schedTimer: number | null = null;
let nextStepTime = 0;
let step16 = 0;
let barIndex = 0;
const LOOKAHEAD_S = 0.12;
const TICK_MS = 25;

function lowpass(freq: number, q = 0.8): BiquadFilterNode {
  const f = ctx!.createBiquadFilter();
  f.type = 'lowpass';
  f.frequency.value = freq;
  f.Q.value = q;
  return f;
}

/** Eine Oszillator-Stimme mit Hüllkurve (Attack→Decay) und optionalem Lowpass. */
function mOsc(freq: number, type: OscillatorType, at: number, dur: number, attack: number, peak: number, cutoff?: number): void {
  const o = ctx!.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(freq, at);
  const g = ctx!.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  // Signalkette: Oszillator → (optional Lowpass) → Hüllkurve → Musik-Bus.
  if (cutoff) {
    const lp = lowpass(cutoff);
    o.connect(lp);
    lp.connect(g);
  } else {
    o.connect(g);
  }
  g.connect(musicGain!);
  o.start(at);
  o.stop(at + dur + 0.03);
}

function mKick(at: number): void {
  const o = ctx!.createOscillator();
  o.type = 'sine';
  o.frequency.setValueAtTime(150, at);
  o.frequency.exponentialRampToValueAtTime(45, at + 0.12);
  const g = ctx!.createGain();
  g.gain.setValueAtTime(0.9, at);
  g.gain.exponentialRampToValueAtTime(0.0001, at + 0.18);
  o.connect(g);
  g.connect(musicGain!);
  o.start(at);
  o.stop(at + 0.2);
}

function mHat(at: number, open = false): void {
  const src = ctx!.createBufferSource();
  src.buffer = whiteNoise();
  const hp = ctx!.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 7000;
  const g = ctx!.createGain();
  const dur = open ? 0.14 : 0.04;
  g.gain.setValueAtTime(0.25, at);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  src.connect(hp);
  hp.connect(g);
  g.connect(musicGain!);
  src.start(at);
  src.stop(at + dur + 0.02);
}

function mSnare(at: number): void {
  const src = ctx!.createBufferSource();
  src.buffer = whiteNoise();
  const bp = ctx!.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 1800;
  const g = ctx!.createGain();
  g.gain.setValueAtTime(0.35, at);
  g.gain.exponentialRampToValueAtTime(0.0001, at + 0.16);
  src.connect(bp);
  bp.connect(g);
  g.connect(musicGain!);
  src.start(at);
  src.stop(at + 0.18);
  // Körper
  const o = ctx!.createOscillator();
  o.type = 'triangle';
  o.frequency.setValueAtTime(180, at);
  const og = ctx!.createGain();
  og.gain.setValueAtTime(0.2, at);
  og.gain.exponentialRampToValueAtTime(0.0001, at + 0.1);
  o.connect(og);
  og.connect(musicGain!);
  o.start(at);
  o.stop(at + 0.12);
}

function scheduleStep(stepIdx: number, when: number, def: BedDef): void {
  if (!ctx || !musicGain) return;
  const stepDur = 60 / def.bpm / 4;
  const chordNotes = def.chords[barIndex % def.chords.length]!;
  const root = chordNotes[0]!;

  // Pad: einmal pro Takt (Schritt 0), hält den ganzen Takt.
  if (def.pad && stepIdx === 0) {
    const dur = stepDur * 16;
    for (const n of chordNotes) {
      mOsc(midi(n), def.padType, when, dur, dur * 0.25, 0.05, 1400);
      mOsc(midi(n) * 1.003, def.padType, when, dur, dur * 0.25, 0.03, 1400); // leichtes Detune
    }
  }

  // Bass: auf jedem Beat (0,4,8,12); Energetic/DeepHouse zusätzlich Offbeats.
  const bassSteps = def.bpm >= 120 ? [0, 4, 6, 8, 12, 14] : [0, 4, 8, 12];
  if (bassSteps.includes(stepIdx)) {
    mOsc(midi(root + def.bassOctave), 'sawtooth', when, stepDur * 1.6, 0.006, 0.16, 320);
  }

  // Arp / Chord-Stabs.
  if (def.arpEvery >= 8) {
    // Stabs auf Offbeat-Schritten (2 und 10).
    if (stepIdx === 2 || stepIdx === 10) {
      for (const n of chordNotes) mOsc(midi(n + 12), def.arpType, when, stepDur * 2, 0.006, 0.05, 2600);
    }
  } else if (stepIdx % def.arpEvery === 0) {
    const idx = (stepIdx / def.arpEvery) % chordNotes.length;
    const note = chordNotes[idx]! + 12;
    mOsc(midi(note), def.arpType, when, stepDur * 1.4, 0.005, 0.06, 3000);
  }

  // Drums.
  if (def.kick.includes(stepIdx)) mKick(when);
  if (def.snare.includes(stepIdx)) mSnare(when);
  if (def.hat.includes(stepIdx)) mHat(when, def.bpm >= 120 && stepIdx % 4 === 2);
}

function scheduler(): void {
  if (!ctx || bed === 'off') return;
  const def = BEDS[bed];
  const stepDur = 60 / def.bpm / 4;
  while (nextStepTime < ctx.currentTime + LOOKAHEAD_S) {
    // Swing: jede zweite 16tel leicht verzögern (nur Lounge).
    const swingOffset = def.swing && step16 % 2 === 1 ? stepDur * 0.18 : 0;
    scheduleStep(step16, nextStepTime + swingOffset, def);
    nextStepTime += stepDur;
    step16 = (step16 + 1) % 16;
    if (step16 === 0) barIndex++;
  }
}

export function wantMusic(on: boolean): void {
  musicWanted = on;
  if (on) startMusic();
  else stopMusic();
}

function startMusic(): void {
  // Eigene Musikdatei bevorzugen (überschreibt die Beds).
  if (custom.music) {
    if (!musicAudio) {
      musicAudio = new Audio(custom.music);
      musicAudio.loop = true;
      musicAudio.volume = 0.4;
    }
    void musicAudio.play().catch(() => {});
    return;
  }
  if (bed === 'off' || !ensureCtx()) return;
  if (schedTimer !== null) return;
  // Musik sanft einblenden.
  const now = ctx!.currentTime;
  musicGain!.gain.cancelScheduledValues(now);
  musicGain!.gain.setValueAtTime(Math.max(0.0001, musicGain!.gain.value), now);
  musicGain!.gain.exponentialRampToValueAtTime(0.5, now + 1.2);
  step16 = 0;
  barIndex = 0;
  nextStepTime = now + 0.08;
  schedTimer = window.setInterval(scheduler, TICK_MS);
}

function stopMusic(): void {
  if (schedTimer !== null) {
    clearInterval(schedTimer);
    schedTimer = null;
  }
  if (ctx && musicGain) {
    const now = ctx.currentTime;
    musicGain.gain.cancelScheduledValues(now);
    musicGain.gain.setValueAtTime(Math.max(0.0001, musicGain.gain.value), now);
    musicGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
  }
  if (musicAudio) {
    musicAudio.pause();
    musicAudio.currentTime = 0;
  }
}
